import React, { useRef, useState, useEffect } from 'react';
import { Camera, Loader2, X, Plus } from 'lucide-react';
import ProductExtractionResults from './ProductExtractionResults';
import PhotoValidationModal from './PhotoValidationModal';

const API_URL = import.meta.env.VITE_API_URL || '';

interface ExtractedProduct {
  name: string;
  brand?: string | null;
  category?: string | null;
  confidence: number;
  quantity?: number | null;
}

interface UploadedImage {
  s3_key: string;
  url: string;
  extracted_products: ExtractedProduct[];
  extraction_status: 'pending' | 'processing' | 'completed' | 'failed';
  image_quality?: string | null;
  upload_timestamp: string;
  localPreview?: string;
}

interface MobileCameraUploadProps {
  sessionId: number;
  questionId: string;
  questionText?: string;
  maxImages?: number;
  onImagesChange: (images: UploadedImage[]) => void;
  images: UploadedImage[];
  disabled?: boolean;
  onTextFallback?: (text: string) => void;
  textFallback?: string;
}

const MobileCameraUpload: React.FC<MobileCameraUploadProps> = ({
  sessionId,
  questionId,
  questionText = "",
  maxImages = 3,
  onImagesChange,
  images,
  disabled = false,
  onTextFallback,
  textFallback = ''
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const objectUrlsRef = useRef<string[]>([]);

  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTextFallback, setShowTextFallback] = useState(false);
  const [fallbackReason, setFallbackReason] = useState<string>('');
  const [textInput, setTextInput] = useState(textFallback);
  const [validationModal, setValidationModal] = useState<{
    isOpen: boolean;
    error: string;
    s3Key: string;
  }>({ isOpen: false, error: '', s3Key: '' });

  useEffect(() => {
    setTextInput(textFallback);
  }, [textFallback]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Revoke all object URLs we created
      objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;

    const availableSlots = maxImages - images.length;
    if (availableSlots <= 0) {
      setError(`Maximum of ${maxImages} images reached`);
      return;
    }

    setIsUploading(true);
    setError(null);

    // Create new abort controller for this batch
    abortControllerRef.current = new AbortController();

    const filesToUpload = Array.from(files).slice(0, availableSlots);

    for (const file of filesToUpload) {
      if (file.size > 15 * 1024 * 1024) {
        setError('File size exceeds 15MB limit');
        continue;
      }

      const localPreview = URL.createObjectURL(file);
      objectUrlsRef.current.push(localPreview);

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('question_text', questionText);

        const response = await fetch(
          `${API_URL}/api/photo-upload/upload-direct/${sessionId}/${questionId}`,
          {
            method: 'POST',
            body: formData,
            credentials: 'include',
            signal: abortControllerRef.current.signal
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Upload failed');
        }

        const result = await response.json();

        const uploadedImage: UploadedImage = {
          ...result,
          localPreview
        };

        // Check if text fallback is needed
        if (result.show_text_fallback) {
          setShowTextFallback(true);
          setFallbackReason(result.rejection_reason || '');
        }

        // Check if needs validation modal
        const needsValidation =
          result.extraction_status === 'failed' ||
          result.image_quality === 'poor' ||
          result.image_quality === 'unclear';

        // Always add to images first
        const newImages = [...images, uploadedImage];
        onImagesChange(newImages);

        // Then show validation modal if needed (after state update)
        if (needsValidation && !result.show_text_fallback) {
          setValidationModal({
            isOpen: true,
            error: result.rejection_reason || 'Image not recognized. Please take a clearer photo.',
            s3Key: uploadedImage.s3_key
          });
        }

        // Update local reference for next iteration
        images = newImages;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Request was aborted, clean up and exit loop
          URL.revokeObjectURL(localPreview);
          objectUrlsRef.current = objectUrlsRef.current.filter(u => u !== localPreview);
          break;
        }
        URL.revokeObjectURL(localPreview);
        objectUrlsRef.current = objectUrlsRef.current.filter(u => u !== localPreview);
        setError(err instanceof Error ? err.message : 'Upload failed');
      }
    }

    setIsUploading(false);
    // Always reset file input to allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (s3Key: string) => {
    const imageToRemove = images.find(img => img.s3_key === s3Key);
    if (imageToRemove?.localPreview) {
      URL.revokeObjectURL(imageToRemove.localPreview);
      objectUrlsRef.current = objectUrlsRef.current.filter(u => u !== imageToRemove.localPreview);
    }
    const updatedImages = images.filter(img => img.s3_key !== s3Key);
    onImagesChange(updatedImages);
  };

  const handleRetake = () => {
    const s3Key = validationModal.s3Key;
    setValidationModal({ isOpen: false, error: '', s3Key: '' });
    handleRemoveImage(s3Key);
    // User will tap "Take Photo" button to retake - more reliable on mobile
  };

  const handleContinueAnyway = () => {
    setValidationModal({ isOpen: false, error: '', s3Key: '' });
  };

  const canAddMore = images.length < maxImages;

  // Only disable during active upload - extraction happens in background
  const isProcessing = isUploading;

  // Poll for extraction results when any image is still processing
  const hasProcessingImages = images.some(img => img.extraction_status === 'processing');
  useEffect(() => {
    if (!hasProcessingImages) return;

    const pollInterval = setInterval(async () => {
      const processingImages = images.filter(img => img.extraction_status === 'processing');
      if (processingImages.length === 0) {
        clearInterval(pollInterval);
        return;
      }

      let hasUpdates = false;
      let triggeredFallback = false;
      let triggeredFallbackReason = '';
      const updatedImages = await Promise.all(
        images.map(async (img) => {
          if (img.extraction_status !== 'processing') return img;

          try {
            const response = await fetch(
              `${API_URL}/api/photo-upload/extraction-status/${encodeURIComponent(img.s3_key)}`,
              { credentials: 'include' }
            );
            if (response.ok) {
              const result = await response.json();
              if (result.status === 'completed') {
                hasUpdates = true;
                if (result.show_text_fallback) {
                  triggeredFallback = true;
                  triggeredFallbackReason = result.rejection_reason || triggeredFallbackReason;
                }
                return {
                  ...img,
                  extraction_status: result.extraction_status,
                  extracted_products: result.extracted_products || [],
                  image_quality: result.image_quality
                };
              }
            }
          } catch (err) {
            console.error('Polling error:', err);
          }
          return img;
        })
      );

      if (hasUpdates) {
        onImagesChange(updatedImages);
        if (triggeredFallback) {
          setShowTextFallback(true);
          if (triggeredFallbackReason) setFallbackReason(triggeredFallbackReason);
        }
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [hasProcessingImages, images, onImagesChange]);

  const inputId = `camera-input-${questionId}`;

  return (
    <div className="space-y-4">
      <input
        id={inputId}
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCapture}
        className="hidden"
      />

      {images.length === 0 ? (
        <label
          htmlFor={disabled || isUploading ? undefined : inputId}
          className={`w-full py-8 border-2 border-dashed border-[#07E5D1] rounded-xl
                     bg-[#E8FCFA] hover:bg-[#d0f9f5] transition-colors flex flex-col
                     items-center justify-center gap-3 cursor-pointer
                     ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isUploading ? (
            <>
              <Loader2 className="w-10 h-10 text-[#07E5D1] animate-spin" />
              <span className="text-lg font-medium text-[#06D4C2]">Uploading & Analyzing...</span>
              <span className="text-sm text-[#07E5D1]">Please wait while we process your photo</span>
            </>
          ) : (
            <>
              <Camera className="w-10 h-10 text-[#07E5D1]" />
              <span className="text-lg font-medium text-[#06D4C2]">Take Photo</span>
              <span className="text-sm text-[#07E5D1]">Tap to open camera</span>
            </>
          )}
        </label>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            {images.map((image) => (
              <div key={image.s3_key} className="relative bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="aspect-video relative bg-gray-100">
                  <img
                    src={image.localPreview || `${API_URL}${image.url}`}
                    alt="Uploaded photo"
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => handleRemoveImage(image.s3_key)}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full
                               hover:bg-red-600 transition-colors shadow-md"
                  >
                    <X className="w-4 h-4" />
                  </button>

                  {image.extraction_status === 'processing' && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="bg-white rounded-lg px-4 py-2 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-[#07E5D1]" />
                        <span className="text-sm font-medium">Analyzing...</span>
                      </div>
                    </div>
                  )}
                </div>

                {image.extraction_status === 'completed' && image.extracted_products?.length > 0 && (
                  <ProductExtractionResults
                    products={image.extracted_products}
                    showImage={false}
                  />
                )}

                {image.extraction_status === 'failed' && (
                  <div className="p-2 bg-amber-50 text-amber-700 text-sm">
                    No products detected
                  </div>
                )}
              </div>
            ))}
          </div>

          {canAddMore && (
            <label
              htmlFor={disabled || isProcessing ? undefined : inputId}
              className={`w-full py-4 border-2 border-dashed rounded-xl transition-colors
                         flex items-center justify-center gap-2 cursor-pointer
                         ${isProcessing
                           ? 'border-[#07E5D1] bg-[#E8FCFA] cursor-wait'
                           : disabled
                             ? 'opacity-50 cursor-not-allowed border-gray-300 bg-gray-50'
                             : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-[#07E5D1]'}`}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-[#07E5D1]" />
                  <span className="text-[#06D4C2] font-medium">
                    {isUploading ? 'Uploading & Analyzing...' : 'Analyzing image...'}
                  </span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-600 font-medium">Add Another Photo</span>
                </>
              )}
            </label>
          )}
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 text-center">{error}</p>
      )}

      {/* Text fallback when image validation/extraction fails */}
      {showTextFallback && onTextFallback && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
          <p className="text-sm font-medium text-amber-800">Photo not recognized</p>
          <p className="text-xs text-amber-600">
            {fallbackReason || "We couldn't identify products in your photo. You can type your answer instead."}
          </p>
          <div className="space-y-2">
            <textarea
              value={textInput}
              onChange={(e) => {
                const next = e.target.value;
                setTextInput(next);
                onTextFallback(next);
              }}
              placeholder="Type your answer here..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#07E5D1]"
            />
            {textInput.trim().length > 0 && (
              <p className="text-xs text-emerald-700">
                Saved — you can continue to the next question.
              </p>
            )}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500 text-center">
        {images.length} of {maxImages} photos
      </p>

      <PhotoValidationModal
        isOpen={validationModal.isOpen}
        error={validationModal.error}
        onRetake={handleRetake}
        onContinueAnyway={handleContinueAnyway}
      />
    </div>
  );
};

export default MobileCameraUpload;
